// components/context/AuthContext.jsx

import { createContext, useContext, useEffect, useState } from 'react';
import { useDispatch } from "react-redux";
import { setDefaultKategorie } from "../redux/filterState";

const AuthContext = createContext();

export const useAuth = () => {
    return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
    const LOCAL_STORAGE_ACCESS_TOKEN_KEY = 'api_access_token';
    const LOCAL_STORAGE_REFRESH_TOKEN_KEY = 'api_refresh_token';

    const [accessToken, setAccessToken] = useState(sessionStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY));
    const [refreshToken, setRefreshToken] = useState(localStorage.getItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY));
    const dispatch = useDispatch();

    const [isAdmin, setIsAdmin] = useState(false);
    const [user, setUser] = useState(null);
    const [mandantId, setMandantId] = useState(null);
    const [mandantName, setMandantName] = useState(null);

    const decodeToken = (token) => {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(c =>
                '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
            ).join(''));
            return JSON.parse(jsonPayload);
        } catch (e) {
            console.error("Token-Decode-Fehler:", e);
            return null;
        }
    };

    const handleLogin = async ({ user, pwd }) => {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;   // 'http://127.0.0.1:4848'

        try {
            const response = await fetch(`${backendUrl}/api/token/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json;charset=utf-8',
                },
                body: JSON.stringify({ username: user, password: pwd }),
            });

            if (!response.ok) throw new Error('Login fehlgeschlagen');

            const data = await response.json();
            const decoded = decodeToken(data.access);

            localStorage.setItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY, data.refresh);
            sessionStorage.setItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY, data.access);

            setAccessToken(data.access);
            setRefreshToken(data.refresh);
            setIsAdmin(decoded?.is_admin || false);
            setUser(decoded?.user || null);
            setMandantId(decoded?.mandant_id || null);
            setMandantName(decoded?.mandant_name || null);

            return 2; // success
        } catch (error) {
            console.error('Login fehlgeschlagen:', error);
            return 3; // error
        }
    };

    useEffect(() => {
        if (!accessToken) return;

        const fetchUserProfile = async () => {
            try {
            const [profilRes, settingsRes] = await Promise.all([
                fetchWithAuth(`/api/usr/profil/`, {
                headers: {
                    'Content-Type': 'application/json'
                }
                }),
                fetchWithAuth(`/api/usr/settings/`, {
                headers: {
                    'Content-Type': 'application/json'
                }
                })
            ]);

            if (!profilRes.ok || !settingsRes.ok) {
                throw new Error('Profil oder Settings konnten nicht geladen werden');
            }

            const profilData = await profilRes.json();
            const settingsData = await settingsRes.json();

            // 1. profile state
            setUser(profilData);

            // 2. default category → Redux
            if (settingsData?.default_kategorie != null) {
                dispatch(setDefaultKategorie(settingsData.default_kategorie));
            }

        } catch (err) {
            console.error("Fehler beim Laden des Profils:", err);
        }
    };

    fetchUserProfile();
    }, [accessToken]);

    const handleLogout = async () => {
        localStorage.removeItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY);
        sessionStorage.clear();     // SessionStorage komplett löschen
        setAccessToken(null);
        setRefreshToken(null);
        setIsAdmin(false);
        setUser(null);
        setMandantId(null);
        setMandantName(null);
        return 0;
    }

    const refreshAccessToken = async () => {
        if (!refreshToken) return;

        const backendUrl = process.env.REACT_APP_BACKEND_URL;

        try {
            const response = await fetch(`${backendUrl}/api/token/refresh/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json;charset=utf-8' },
                body: JSON.stringify({ refresh: refreshToken }),
            });

            if (!response.ok) throw new Error('Token-Refresh fehlgeschlagen');

            const data = await response.json();
            const decoded = decodeToken(data.access);

            sessionStorage.setItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY, data.access);
            setAccessToken(data.access);
            setIsAdmin(decoded?.is_admin || false);
            setUser(decoded?.user || null);
            setMandantId(decoded?.mandant_id || null);
            setMandantName(decoded?.mandant_name || null);

        } catch (error) {
            console.error('Token-Refresh-Fehler:', error);
        }
    };

    // central helper function: do all API calls, renew the access token with refresh token
    const fetchWithAuth = async (url, options = {}) => {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const fullUrl = `${backendUrl}${url}`;
        let token = sessionStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY);

        const makeRequest = async () =>
            fetch(fullUrl, {
                ...options,
                headers: {
                    ...(options.headers || {}),
                    Authorization: `Bearer ${token}`,
                },
            });

        let response = await makeRequest();

        if (response.status === 401 && refreshToken) {
            await refreshAccessToken();
            token = sessionStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY);
            response = await makeRequest();
        }

        return response;
    };

    useEffect(() => {
        if (!accessToken && refreshToken) {
            refreshAccessToken();
        }
    }, [refreshToken, accessToken]);

    return (
        <AuthContext.Provider value={{
            accessToken,
            isAdmin,
            user,
            mandantId,
            mandantName,
            handleLogin,
            handleLogout,
            fetchWithAuth
        }}>
            {children}
        </AuthContext.Provider>
    );
};
